/**
 * WeatherChart Component
 *
 * This component creates an interactive weather visualization dashboard that displays various weather metrics
 * over time. It supports multiple chart types including temperature, humidity, wind speed, and wind direction.
 *
 * Key Features:
 * - Interactive zooming and panning
 * - Real-time data updates
 * - Wind direction visualization with arrows
 * - Responsive design for different screen sizes
 * - Multi-language support
 * - Fullscreen mode
 *
 * @component
 * @param {Array} weekWeatherData - Array of weather data points for the week
 */

import React, { useState, useRef, useEffect } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { DateTime } from "luxon";
import "chart.js/auto";
import {
  CircularProgress,
  Box,
  IconButton,
  Modal,
  Paper,
  Grid,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import zoomPlugin from "chartjs-plugin-zoom";
import { Chart } from "chart.js";
import "chartjs-adapter-date-fns";
import { calculateWindChill } from "../WeatherDashboard/calculateWindChill";
import { useTranslation } from "../../context/TranslationContext";

// Register all required Chart.js components and plugins
ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

// Set global font defaults for Chart.js to support Inuktitut syllabics
Chart.defaults.font.family =
  '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif';

/**
 * Main WeatherChart Component
 *
 * This is the primary component that renders the weather visualization dashboard.
 * It processes weather data and creates interactive charts for different weather metrics.
 *
 * @param {Object} props
 * @param {Array} props.weekWeatherData - Array of weather data points for visualization
 * @returns {JSX.Element} Weather visualization dashboard
 */
const WeatherChart = ({
  weekWeatherData = [],
  isFullscreen = false,
  fullscreenContainerRef = null,
}) => {
  // State management for selected chart and chart references
  const [selectedChart, setSelectedChart] = useState(null);
  const chartRefs = useRef([]);
  const { translate, currentLanguage } = useTranslation();

  // Color scheme for different weather metrics
  // NOTE: the fullscreen modal maps a chart's index to its color via
  // Object.keys(variableColors)[selectedChart], so the ORDER of these keys
  // must stay in sync with the order of the `charts` array below.
  const variableColors = {
    temperature: {
      primary: "rgb(255, 99, 132)", // Red for temperature
    },
    humidity: {
      primary: "rgb(54, 162, 235)", // Blue for humidity
    },
    wind: {
      primary: "rgb(75, 192, 192)", // Teal for wind speed
    },
    windGust: {
      primary: "rgb(153, 102, 255)", // Purple for wind gusts
    },
    rainfall: {
      primary: "rgb(0, 150, 199)", // Blue for rainfall (bar)
    },
    visibility: {
      primary: "rgb(96, 125, 139)", // Blue-grey for visibility
    },
    solar: {
      primary: "rgb(255, 159, 64)", // Orange for solar irradiance
    },
    snow: {
      primary: "rgb(0, 188, 212)", // Cyan for snow depth
    },
  };

  /**
   * Creates chart data configuration for a specific weather metric
   *
   * This function generates the data structure required by Chart.js to render weather metrics.
   * It handles both single and dual data series, with appropriate styling and formatting.
   *
   * @param {Array} timestamps - Array of timestamp values for x-axis
   * @param {Array} primaryData - Primary data values (e.g., temperature)
   * @param {Array} [secondaryData=null] - Optional secondary data values (e.g., feels like temperature)
   * @param {Array} labels - Labels for the data series
   * @param {string} colorKey - Key to determine the color scheme from variableColors
   * @param {boolean} [isZoomed=false] - Whether the chart is in zoomed state
   * @returns {Object} Chart.js data configuration object
   */
  const createChartData = (
    timestamps,
    primaryData,
    secondaryData = null,
    labels,
    colorKey,
    isZoomed = false
  ) => ({
    labels: timestamps,
    datasets: [
      {
        label: labels[0],
        data: primaryData,
        borderColor: variableColors[colorKey].primary,
        backgroundColor: variableColors[colorKey].primary, // used as fill for bar charts
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: isZoomed ? 4 : 0, // Show points only in zoomed view
        pointHoverRadius: isZoomed ? 6 : 0, // Show hover points only in zoomed view
        yAxisID: "y",
      },
      secondaryData && {
        label: labels[1],
        data: secondaryData,
        borderColor: "rgb(54, 100, 235)",
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: isZoomed ? 4 : 0, // Show points only in zoomed view
        pointHoverRadius: isZoomed ? 6 : 0, // Show hover points only in zoomed view
        yAxisID: "y",
        borderDash: [1, 1], // Optional: makes the secondary line dashed
      },
    ].filter(Boolean), // Remove null/undefined datasets
  });

  /**
   * Creates a custom plugin for drawing wind direction arrows on the chart
   *
   * This plugin adds visual indicators for wind direction using arrows and cardinal directions.
   * It automatically adjusts the number of arrows based on the zoom level to maintain readability.
   *
   * @param {Array} directionData - Array of wind direction values in degrees
   * @returns {Object} Chart.js plugin configuration
   */
  const createArrowPlugin = (directionData) => {
    /**
     * Converts wind direction in degrees to cardinal direction
     *
     * @param {number} degrees - Wind direction in degrees
     * @returns {string} Cardinal direction (N, NE, E, etc.)
     */
    const degreesToCardinal = (degrees) => {
      const directions = [
        "N",
        "NNE",
        "NE",
        "ENE",
        "E",
        "ESE",
        "SE",
        "SSE",
        "S",
        "SSW",
        "SW",
        "WSW",
        "W",
        "WNW",
        "NW",
        "NNW",
      ];
      const index = Math.round(((degrees + 360) % 360) / 22.5) % 16;
      return directions[index];
    };

    return {
      id: "windDirectionArrows",
      afterDatasetsDraw: (chart) => {
        const { ctx } = chart;
        const xAxis = chart.scales.x;
        const yAxis = chart.scales.y;
        const chartArea = chart.chartArea;

        // Get visible range
        const visibleRange = {
          min: xAxis.min,
          max: xAxis.max,
        };

        // Calculate visible data points
        const visiblePoints = timestamps.filter((t) => {
          const timestamp = t.getTime();
          return timestamp >= visibleRange.min && timestamp <= visibleRange.max;
        });

        // Show only 5 arrows evenly spaced
        const maxArrows = 5;
        const step = Math.max(1, Math.floor(visiblePoints.length / maxArrows));

        // Calculate arrow area dimensions
        const topMargin = 15; // Adjust this value to increase/decrease top space

        const arrowAreaTop = chartArea.top + topMargin; // Position arrows below the title

        visiblePoints.forEach((timestamp, index) => {
          if (index % step !== 0) return;

          const dataIndex = timestamps.findIndex(
            (t) => t.getTime() === timestamp.getTime()
          );
          const direction = directionData[dataIndex];

          if (direction !== null && direction !== undefined) {
            const x = xAxis.getPixelForValue(timestamp);

            // Skip if outside chart area
            if (x < chartArea.left || x > chartArea.right) return;
            const adjustedDirection = (direction + 180) % 360;

            // Draw arrow
            ctx.save();
            ctx.translate(x, arrowAreaTop);
            ctx.rotate((adjustedDirection * Math.PI) / 180);

            // Draw arrow with trailing shaft
            ctx.beginPath();

            // Draw the trailing shaft line
            ctx.moveTo(0, 8); // Start of shaft
            ctx.lineTo(0, -8); // End of shaft/arrow head

            // Draw the arrow head
            ctx.moveTo(-4, -3); // Left point of arrow head
            ctx.lineTo(0, -8); // Top point
            ctx.lineTo(4, -3); // Right point of arrow head

            ctx.strokeStyle = "rgba(0, 0, 0, 0.6)";
            ctx.lineWidth = 3;
            ctx.stroke();
            ctx.restore();

            // Draw cardinal direction
            ctx.save();
            ctx.textAlign = "center";
            ctx.textBaseline = "top";
            ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
            ctx.font =
              '13px "Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif';
            ctx.fillText(degreesToCardinal(direction), x, arrowAreaTop + 15);
            ctx.restore();
          }
        });
      },
    };
  };

  /**
   * Creates chart options configuration including zoom, tooltips, and styling
   *
   * This function configures the appearance and behavior of the charts.
   * It handles responsive design, zoom behavior, tooltips, and axis formatting.
   *
   * @param {string} title - Chart title
   * @param {string} primaryUnit - Unit of measurement for the primary data
   * @param {boolean} [hasDirections=false] - Whether the chart includes wind direction arrows
   * @returns {Object} Chart.js options configuration
   */
  const createChartOptions = (title, primaryUnit, hasDirections = false) => {
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);
    const oneHour = 60 * 60 * 1000;

    // Responsive padding
    const isMobile = window.innerWidth < 768;
    const basePadding = isMobile ? 10 : 30; // smaller on mobile

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: hasDirections ? basePadding : isMobile ? 5 : 15,
          right: hasDirections ? basePadding : isMobile ? 5 : 15,
          top: isMobile ? 0 : 0,
          bottom: isMobile ? 0 : 0,
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        tooltip: {
          titleFont: {
            family:
              '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
          },
          bodyFont: {
            family:
              '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
          },
          callbacks: {
            title: function (context) {
              const timestamp = context[0].parsed.x;
              return DateTime.fromMillis(timestamp).toFormat(
                "MMM dd, yyyy HH:mm ZZZZ"
              );
            },
            label: function (context) {
              const value = context.parsed.y;
              return `${context.dataset.label}: ${
                typeof value === "number" ? value.toFixed(1) : value
              } ${primaryUnit}`;
            },
          },
        },
        legend: {
          position: "top",
          labels: {
            boxWidth: 20,
            font: {
              size: window.innerWidth < 768 ? 10 : 12,
              family:
                '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
            },
          },
        },
        title: {
          display: true,
          text: title,
          font: {
            size: window.innerWidth < 768 ? 14 : 16,
            weight: "bold",
            family:
              '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
          },
        },
        zoom: {
          limits: {
            x: {
              min: minTime,
              max: maxTime,
              minRange: oneHour,
            },
          },
          zoom: {
            wheel: {
              enabled: true,
              speed: 0.1,
            },
            pinch: { enabled: true },
            mode: "x",
          },
          pan: {
            enabled: true,
            mode: "x",
          },
        },
      },
      scales: {
        x: {
          type: "time",
          time: {
            unit: "hour",
            displayFormats: {
              hour: "HH:mm",
            },
            tooltipFormat: "MMM dd, yyyy HH:mm",
          },
          min: minTime,
          max: maxTime,
          ticks: {
            source: "auto",
            autoSkip: true,
            // Slightly lower than 7 so a week of data can skip a day when the plot
            // is narrow (e.g. y-axis title "km/h" vs "°C"). Matches top-row charts.
            autoSkipPadding: 12,
            maxTicksLimit: window.innerWidth < 768 ? 4 : 7,
            maxRotation: 0,
            font: {
              size: window.innerWidth < 768 ? 8 : 10,
              family:
                '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
            },
            callback: function (value, index, ticks) {
              // Replace moment with Luxon
              const date = DateTime.fromMillis(value);
              const isScrolling =
                this.chart.scales.x.min !== minTime ||
                this.chart.scales.x.max !== maxTime;

              if (isScrolling) {
                // Show date with time and timezone
                return [date.toFormat("LLL dd"), `(${date.toFormat("HH:mm")})`];
              }
              // Return a string (not a one-element array) so Chart.js measures label
              // width correctly for autoSkip; arrays are for multi-line ticks only.
              return date.toFormat("LLL dd");
            },
          },
          grid: {
            display: true,
          },
          border: {
            display: true,
          },
        },
        y: {
          position: "left",
          title: {
            display: true,
            text: primaryUnit,
            font: {
              size: window.innerWidth < 768 ? 10 : 12,
              family:
                '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
            },
          },
          ticks: {
            font: {
              size: window.innerWidth < 768 ? 8 : 10,
              family:
                '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
            },
            callback: function (value) {
              // Check if the value is a number and has decimals
              if (typeof value === "number" && !Number.isInteger(value)) {
                return value.toFixed(1); // Limit to 1 decimal places
              }
              return value;
            },
          },
          // Add extra space at the top for arrows
          suggestedMax: function (context) {
            const maxValue = Math.max(...context.chart.data.datasets[0].data);
            return hasDirections ? maxValue * 1.2 : maxValue * 1.1; // 20% extra space for charts with arrows
          },
        },
      },
    };
  };

  /**
   * Processes raw weather data with quality control
   *
   * This function transforms raw weather data into a format suitable for charting.
   * It implements quality control checks and handles missing or invalid data points.
   *
   * @param {Array} weekWeatherData - Raw weather data array
   * @returns {Object} Processed data object with timestamps and weather metrics
   */
  if (!Array.isArray(weekWeatherData) || weekWeatherData.length === 0) {
    return <CircularProgress />;
  }

  //   const timestamps = weekWeatherData.map(entry => moment(entry.timestamp).format('HH:mm'));

  // Modify timestamps creation to use Date objects instead of formatted strings
  const timestamps = weekWeatherData.map((entry) =>
    DateTime.fromISO(entry.timestamp).toJSDate()
  );

  /**
   * Builds a QC-filtered single series for a given element key.
   * Mirrors the QC logic used by the existing charts: only keep a value when
   * its qc_value is undefined or 100, preserve legitimate zeros, else null.
   *
   * @param {string} key - element key (e.g. "surface_air_pressure")
   * @param {Function} [transform] - optional value transform (e.g. unit conversion)
   * @returns {Array<number|null>}
   */
  const qcFilteredSeries = (key, transform = (v) => v) =>
    weekWeatherData.map((entry) => {
      const el = entry.elements[key];
      if (el === undefined || el === null) return null;

      const qcValue = el.qc_value;
      if (!(qcValue === undefined || qcValue === 100)) return null;

      const value = el.value;
      if (value === undefined || value === null) return null;

      return value === 0 ? 0 : transform(value);
    });

  const charts = [
    {
      data: createChartData(
        timestamps,
        // weekWeatherData.map(
        //   (entry) => entry.elements.temperature_ext?.value || 0
        // ),

        weekWeatherData.map((entry) => {
          const temperature_ext = entry.elements.temperature_ext;
          if (temperature_ext === undefined || temperature_ext === null)
            return null;

          const qcValue = temperature_ext.qc_value;

          return qcValue === undefined || qcValue === 100
            ? temperature_ext.value === 0 //the actual temperature_ext value could be 0 too
              ? 0
              : temperature_ext.value || null
            : null;
        }),

        weekWeatherData.map((entry) => {
          const temperature_ext = entry.elements.temperature_ext;
          const wind_speed = entry.elements.wind_speed;

          // Check if temperature_ext exists
          if (temperature_ext === undefined || temperature_ext === null)
            return null;

          const tempQcValue = temperature_ext.qc_value;
          if (!(tempQcValue === undefined || tempQcValue === 100)) return null;

          // Calculate wind chill if temperature check passed
          const windChill = calculateWindChill(temperature_ext, wind_speed);
          return windChill === 0 ? 0 : windChill || null;
        }),

        [translate("Temperature"), translate("Wind Chill")],
        "temperature"
      ),
      options: createChartOptions(
        `${translate("Temperature")} & ${translate("Wind Chill")}`,
        "°C",
        true
      ),
    },
    {
      data: createChartData(
        timestamps,
        // weekWeatherData.map((entry) => entry.elements.humidity_ext?.value || 0),
        weekWeatherData.map((entry) => {
          const humidity_ext = entry.elements.humidity_ext;
          if (humidity_ext === undefined || humidity_ext === null) return null;

          const qcValue = humidity_ext.qc_value;
          // Explicitly check for undefined or 100 qcValue

          return qcValue === undefined || qcValue === 100
            ? humidity_ext.value === 0 //the actual humidity_ext value could be 0 too
              ? 0
              : humidity_ext.value || null
            : null;
        }),

        null,
        [translate("Humidity")],
        "humidity"
      ),
      options: createChartOptions(translate("Humidity"), "%", null, false), // No directions
    },
    {
      data: createChartData(
        timestamps,
        // weekWeatherData.map((entry) => entry.elements.wind_speed?.value || 0),
        weekWeatherData.map((entry) => {
          const wind_speed = entry.elements.wind_speed;
          if (wind_speed === undefined || wind_speed === null) return null;
          const qcValue = wind_speed?.qc_value;
          // Return the wind_speed value only if qcValue is undefined or 100

          return qcValue === undefined || qcValue === 100
            ? wind_speed.value === 0 //the actual wind_speed value could be 0 too
              ? 0
              : wind_speed.value * 3.6 || null
            : null;
        }),
        null,
        [translate("Wind Speed")],
        "wind"
      ),
      options: createChartOptions(translate("Wind Speed"), "km/h", true), // Add hasDirections flag
      plugins: [
        createArrowPlugin(
          // weekWeatherData.map(
          //   (entry) => entry.elements.wind_direction?.value || 0
          // )
          weekWeatherData.map((entry) => {
            const wind_direction = entry.elements.wind_direction;
            if (wind_direction === undefined || wind_direction === null)
              return null;
            const qcValue = wind_direction?.qc_value;
            // Return the wind_direction value only if qcValue is undefined or 100
            return qcValue === undefined || qcValue === 100
              ? wind_direction.value === 0 //the actual wind_direction value could be 0 too
                ? 0
                : wind_direction.value || null
              : null;
          })
        ),
      ],
    },
    {
      data: createChartData(
        timestamps,
        // weekWeatherData.map(
        //   (entry) => entry.elements.wind_gust_speed?.value || 0
        // ),
        weekWeatherData.map((entry) => {
          const wind_gust_speed = entry.elements.wind_gust_speed;
          if (!wind_gust_speed) return null;
          const qcValue = wind_gust_speed?.qc_value;
          // Return the wind_gust_speed value only if qcValue is undefined or 100
          return qcValue === undefined || qcValue === 100
            ? wind_gust_speed.value === 0 //the actual wind_gust_speed value could be 0 too
              ? 0
              : wind_gust_speed.value * 3.6 || null
            : null;
        }),

        null,
        [translate("Wind Gust Speed")],
        "windGust"
      ),
      options: createChartOptions(translate("Wind Gust Speed"), "km/h", true), // Add hasDirections flag
      plugins: [
        createArrowPlugin(
          // weekWeatherData.map(
          //   (entry) => entry.elements.wind_gust_direction?.value || 0
          // )
          weekWeatherData.map((entry) => {
            const wind_gust_direction = entry.elements.wind_gust_direction;
            if (!wind_gust_direction) return null;
            const qcValue = wind_gust_direction?.qc_value;

            return qcValue === undefined || qcValue === 100
              ? wind_gust_direction.value === 0 //the actual wind_gust_direction value could be 0 too
                ? 0
                : wind_gust_direction.value || null
              : null;
          })
        ),
      ],
    },
    // --- AWS V2 additional charts ---
    // Order here MUST match the trailing keys of variableColors above, because
    // the fullscreen modal derives colorKey from the chart index.
    {
      // colorKey: "rainfall" — bar graph
      chartType: "bar",
      data: createChartData(
        timestamps,
        qcFilteredSeries("thickness_of_rainfall_amount"),
        null,
        [translate("Rainfall")],
        "rainfall"
      ),
      options: createChartOptions(translate("Rainfall"), "mm", false),
    },
    {
      // colorKey: "visibility"
      data: createChartData(
        timestamps,
        qcFilteredSeries("visibility_in_air"),
        null,
        [translate("Visibility")],
        "visibility"
      ),
      options: createChartOptions(translate("Visibility"), "m", false),
    },
    {
      // colorKey: "solar"
      data: createChartData(
        timestamps,
        qcFilteredSeries("surface_downwelling_shortwave_flux"),
        null,
        [translate("Solar")],
        "solar"
      ),
      options: createChartOptions(translate("Solar"), "W/m²", false),
    },
    {
      // colorKey: "snow"
      data: createChartData(
        timestamps,
        qcFilteredSeries("snow_depth"),
        null,
        [translate("Snow Depth")],
        "snow"
      ),
      options: createChartOptions(translate("Snow Depth"), "cm", false),
    },
  ];

  /**
   * Whether a chart has at least one real value in its primary series.
   * Charts whose series is entirely null/undefined (e.g. a sensor a station
   * does not report) are skipped so we don't render empty plots.
   */
  const hasData = (chart) =>
    chart?.data?.datasets?.[0]?.data?.some(
      (v) => v !== null && v !== undefined
    );

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper
        elevation={1}
        sx={{
          pl: { xs: -1, sm: 0 },
          pr: { xs: 1, sm: 2 },
          pt: { xs: 2, sm: 2 },
          pb: { xs: 2, sm: 2 },
        }}
      >
        <Grid container spacing={2}>
          {charts.map((chart, index) => {
            // Skip charts with no data (e.g. sensors a station does not report).
            // Return null (keeping the original index) so the fullscreen modal's
            // index-based color/data lookup stays aligned.
            if (!hasData(chart)) return null;
            return (
            <Grid item xs={12} sm={6} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  height: "300px",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    display: "flex",
                    gap: 1,
                    zIndex: 1,
                  }}
                >
                  {/* Remove close/reset button */}
                  <IconButton
                    onClick={() => setSelectedChart(index)}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                    }}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Box>
                {chart.chartType === "bar" ? (
                  <Bar
                    ref={(el) => (chartRefs.current[index] = el)}
                    data={chart.data}
                    options={chart.options}
                    plugins={chart.plugins || []}
                  />
                ) : (
                  <Line
                    ref={(el) => (chartRefs.current[index] = el)}
                    data={chart.data}
                    options={chart.options}
                    plugins={chart.plugins || []}
                  />
                )}
              </Paper>
            </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Fullscreen Modal */}
      <Modal
        open={selectedChart !== null}
        onClose={() => setSelectedChart(null)}
        container={
          isFullscreen && fullscreenContainerRef
            ? fullscreenContainerRef.current
            : undefined
        }
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "80%" },
            height: { xs: "80%", sm: "85%", md: "90%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 1,
          }}
        >
          <IconButton
            onClick={() => setSelectedChart(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          {selectedChart !== null &&
            (() => {
              const FullscreenChart =
                charts[selectedChart].chartType === "bar" ? Bar : Line;
              return (
                <Box sx={{ height: "100%", pt: 2 }}>
                  <FullscreenChart
                    data={createChartData(
                      timestamps,
                      charts[selectedChart].data.datasets[0].data,
                      charts[selectedChart].data.datasets[1]?.data, // Include secondary dataset if it exists
                      [
                        charts[selectedChart].data.datasets[0].label,
                        charts[selectedChart].data.datasets[1]?.label,
                      ].filter(Boolean),
                      Object.keys(variableColors)[selectedChart],
                      true
                    )}
                    options={charts[selectedChart].options}
                    plugins={charts[selectedChart].plugins || []}
                  />
                </Box>
              );
            })()}
        </Box>
      </Modal>
    </Box>
  );
};

export default WeatherChart;
